import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ICategoryDto } from '@models/category-dto';
import { ClashListDto } from '@models/clash/clashes/clash-list-dto';
import { SimpleCLashDto } from '@models/embate/clash-dto';
import { RecordClashesForm } from '@models/clash/clashes/record-clashes-form';
import {
  ClashBaseForm,
  ClashRegistrationFightForm,
} from '@models/clash/clashes/registration-fight-dto';
import { GroupKeyDto } from '@models/clash/group-key-dto';
import { ICompleteStepParameterizationDto } from '@models/competition/step-paramaterization/dtos/complete-simple-step-parameterization-dto';
import { InscricaoEtapaDto } from '@models/embate/clash-dto';
import { EventTypeEnum } from '@models/enums/event-type-enum';
import { MetadataPutEnum } from '@models/enums/metadata-put-enum';
import { IEventDto } from '@models/event-dto';
import { ResponseDto } from '@models/exceptions/response-dto';
import { IPageable } from '@models/pagination/pageable';
import { GenderPipe } from '@shared/pipes/naipe.pipe';
import { extractResponse } from '@shared/rxjs/custom-rxjs-operators';
import { ClashService } from '@shared/services/clash.service';
import { ClashRegistrationService } from '@shared/services/competition/clash-registration/clash-registration.service';
import {
  GroupKeyService,
  InscricaoChaveDto,
  InscricaoEtapaCompletaDto,
} from '@shared/services/competition/group-key.service';
import {
  RodadaDto,
  RodadasService,
} from '@shared/services/competition/rodadas.service';
import { StepParameterizationService } from '@shared/services/competition/step-parametrization.service';
import { EventService } from '@shared/services/event.service';
import { LoadingService } from '@shared/services/loading.service';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { BaseComponent } from 'src/app/core/utilities/base-component/base-component';
import { ControlsOf } from './../../../../shared/types/form-group-of';

type IFilteredEnrollments = {
  ichNrId: number;
  incNrId: number;
  incTxNome: string;
  incTxEstado: string;
  cheNrId: number;
};
type EmbateFormControls = {
  cheNrId: FormControl<number | null>;
  cheTxNome: FormControl<string | null>;
  chaveEmbateSimplesDto: FormControl<SimpleCLashDto | null>;
  rodNrId: FormControl<number | null>;
  inscricaoEmbateFormulario: FormArray<
    FormGroup<ControlsOf<ClashRegistrationFightForm>>
  >;
};

type ClashRegistrationFightValue = {
  entNrId?: number | null;
  ichNrId?: number | null;
  iemTxNome?: string | null;
  iemTxNomeDefault?: string | null;
  estTxNome?: string | null;
  cheTxNomeOrigem?: string | null;
  embNrIdOrigem?: number | null;
  iemNrVencedor?: number | null;
  iemBlSorteio?: boolean | null;
  entTxMetadado?: MetadataPutEnum | null;
};

type ClashSaveResponse = Partial<RecordClashesForm> & {
  embNrId?: number;
};

@Component({
  selector: 'sigecom-round-management',
  templateUrl: './round-management.component.html',
})
export class RoundManagementComponent extends BaseComponent {
  participantToAdd = new BehaviorSubject<InscricaoChaveDto | null>(null);
  selectedIncNrId?: number;
  openModalAddParticipant = false;
  tempAddParticipantData?: { side: string; cheNrId?: number };
  embatesForm = this.fb.group({
    embates: this.fb.array([]),
  });
  categoriaDto?: ICategoryDto;
  rodadaDtoList?: RodadaDto[];
  rodadaDtoView?: RodadaDto;
  rodadaCurrentIndex = new BehaviorSubject(0);
  readonly #loadingService = inject(LoadingService);
  readonly #roundService = inject(RodadasService);
  readonly #keyEnrollmentService = inject(GroupKeyService);
  readonly #clashService = inject(ClashService);
  readonly #clashRegistrationService = inject(ClashRegistrationService);
  readonly #stepParametrizationsService = inject(StepParameterizationService);
  readonly #eventService = inject(EventService);
  readonly keysSubject = new BehaviorSubject<GroupKeyDto[]>([]);
  public readonly keys$ = this.keysSubject.asObservable();
  public selectedKey? = new BehaviorSubject<GroupKeyDto | null>(null);
  public readonly petNrId = Number(
    this.activatedRoute.snapshot.paramMap.get('petNrId') || -1,
  );
  eventData?: IEventDto;
  parametrizationDto?: ICompleteStepParameterizationDto;
  public petDescription = '';
  public drawDisabled = false;
  filteredCabecas$?: Observable<InscricaoEtapaCompletaDto[]>;
  public keyEnrollmentsDto: InscricaoEtapaCompletaDto[] = [];
  public qtdIncSelecionados = 0;
  public qtdTotalInc = 0;
  public qtdCabecaChavesPorChave = 0;
  public submittingCabecaChave: number | null = null;

  public embatesRodadaList?: ClashListDto[];
  public isNational = false;

  cheNrIdModalAddCabecaChave?: number;
  cheTxNomeModalAddCabecaChave?: string;
  ichNrIdCabecaChaveAtual?: number;

  cabecaChaveModalOpen = false;

  click$ = new Subject<void>();
  infoDto = {};

  embatesFa = this.fb.group({
    confrontos: this.fb.array<FormGroup<EmbateFormControls>>([]),
  });

  get confrontosFa() {
    return this.embatesFa.controls.confrontos;
  }

  get hasConfrontosNaRodadaAtual(): boolean {
    return this.confrontosFa.controls.some((confronto) =>
      this.showConfrontoForCurrentRodada(confronto),
    );
  }

  keysMap$ = this.keys$.pipe(
    map((keys) =>
      keys.reduce(
        (acc, key) => {
          acc[key.cheNrId] = key.cheTxNome;
          return acc;
        },
        {} as Record<number, string>,
      ),
    ),
  );

  filteredEnrollments?: IFilteredEnrollments[];

  public simpleStepParametrization$ = this.#stepParametrizationsService
    .getStepParametrizationById$(this.petNrId)
    .pipe(
      map((step) => {
        this.parametrizationDto = step;
        const categoria = step.petObjCategoria;
        this.categoriaDto = categoria;
        return {
          info: {
            catNrId: categoria?.catNrId,
            categoriaInfo: categoria
              ? `${categoria.catNrAnoInicio} - ${categoria.catNrAnoFim}`
              : '',
            petTxNome: step.petTxNome,
            formatoInfo: categoria?.catTxTipoInscricao ?? '',
            modalidadeInfo: categoria?.catTxNome ?? '',
          },
        };
      }),

      switchMap(({ info }) =>
        this.#eventService
          .getEventByCatNrId(info.catNrId!)
          .pipe(map((event) => ({ info, event }))),
      ),

      tap(({ info, event }) => {
        const genderPipe = new GenderPipe();
        this.petDescription = `${event?.eveTxNome} - ${genderPipe.transform(this.categoriaDto?.catTxSexo)}`;
        this.isNational = event.eveTxTipo === EventTypeEnum.NACIONAL;
        this.infoDto = info;
        this.eventData = event;
      }),

      map(({ info }) => info),
    );

  ngOnInit(): void {
    this.loadKeys();
    this.loadRounds();
    this.subscribeKeyChange();
  }

  public generateAllClashs() {
    if (this.drawDisabled) {
      return;
    }

    const rodNrId = this.rodadaDtoList?.[this.rodadaCurrentIndex.value].rodNrId;
    this.drawDisabled = true;
    this.#loadingService.activate();

    this.#clashRegistrationService
      .generateClashes(this.petNrId)
      .pipe(
        this.takeUntilDestroy(),
        finalize(() => {
          this.drawDisabled = false;
          this.#loadingService.deactivate();
        }),
      )
      .subscribe({
        next: (_) => {
          this.snackbarService.showSuccess('Confrontos. gerados com sucesso');
          this.getClashs(rodNrId);
        },
        error: (err) => this.messageErrorResolver.resolveMessage(err),
      });
  }

  getIncDisplayName(value?: ClashRegistrationFightValue | null): string {
    // if (value?.iemTxNome && value?.estTxNome) {
    //   return value.estTxNome;
    // }

    if (value?.iemTxNomeDefault && !value?.iemTxNomeDefault?.includes('A Definir')) {
      return value?.iemTxNomeDefault ?? '';
    }
    if (value?.estTxNome) {
      return value.estTxNome;
    }

    return '+';
    // return value?.iemTxNome ? value?.iemTxNomeDefault : value?.estTxNome ?? '+';
  }

  getParticipantKeyName(value?: ClashRegistrationFightValue | null, clashKeyName?: string | null): string {
    return clashKeyName || '';
  }

  getModalityName(cheNrId?: number): string {
    const sport = this.parametrizationDto?.comObjCompeticao?.comObjEsporte?.espTxDescricao;
    const key = cheNrId
      ? this.keysSubject.value.find((item) => item.cheNrId === cheNrId)
      : undefined;
    const genderValue = key?.parametrizacaoEtapaDto?.sorObjCategoria?.catTxSexo
      ?? this.categoriaDto?.catTxSexo;
    const gender = genderValue
      ? String(genderValue).toLowerCase() === 'masculina' ? 'masculino' : 'feminino'
      : '';
    return [sport, gender].filter(Boolean).join(' ') || this.parametrizationDto?.comObjCompeticao?.comTxNome || '-';
  }

  getModalityAcronym(cheNrId?: number): string {
    return this.getModalityName(cheNrId).split(/\s+/).filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase()).join('');
  }

  getModalityAcronymByKey(key?: SimpleCLashDto | null): string {
    const modalidade = key?.comTxNome?.trim()?.charAt(0) ?? '';
    const naipe = key?.catTxSexo?.trim()?.charAt(0) ?? '';
    return `${modalidade}${naipe}`.toUpperCase()
      || this.getModalityAcronym(key?.cheNrId);
  }

  private duplicateIchNrIdValidator = (control: AbstractControl) => {
    if (!(control instanceof FormArray)) {
      return null;
    }

    const fightArray = control as FormArray<
      FormGroup<ControlsOf<ClashRegistrationFightForm>>
    >;

    const ids = fightArray.controls
      .map((fight) => fight.controls.ichNrId?.value)
      .filter((id): id is number => id != null);

    return new Set(ids).size !== ids.length ? { duplicateIchNrId: true } : null;
  };

  private createFightGroup(): FormGroup<
    ControlsOf<ClashRegistrationFightForm>
  > {
    return this.fb.group<ControlsOf<ClashRegistrationFightForm>>({
      entNrId: this.fb.control(null),
      ichNrId: this.fb.control(null),
      embNrIdOrigem: this.fb.control(null),
      iemTxNome: this.fb.control(null),
      iemTxNomeDefault: this.fb.control(null),
      estTxNome: this.fb.control(null),
      iemNrVencedor: this.fb.control(null),
      iemBlSorteio: this.fb.control(null),
      entTxMetadado: this.fb.control(null),
    });
  }

  createEmbate(rodNrId?: number) {
    if (!rodNrId) {
      this.snackbarService.showWarn(
        'Não é possível criar embate sem rodada selecionada',
      );
      return;
    }
    const control = this.fb.group<EmbateFormControls>({
      cheNrId: this.fb.control(null, Validators.required),
      cheTxNome: this.fb.control(null, Validators.required),
      chaveEmbateSimplesDto: this.fb.control(null),
      rodNrId: this.fb.control(rodNrId, Validators.required),
      inscricaoEmbateFormulario: this.fb.array(
        [this.createFightGroup(), this.createFightGroup()],
        this.duplicateIchNrIdValidator,
      ),
    });
    this.confrontosFa.insert(0, control);
  }

  addParticipant(sideLabel: 'A' | 'B', index: number) {
    const participant = this.participantToAdd.value;

    if (!participant) {
      this.snackbarService.showWarn(
        'Você precisa selecionar ao menos um participante',
      );
      return;
    }

    const side = sideLabel === 'A' ? 0 : 1;

    const confronto = this.confrontosFa.at(index);
    const existingCheNrId = confronto.controls.cheNrId.value;

    if (existingCheNrId && existingCheNrId !== participant.cheNrId) {
      this.snackbarService.showWarn(
        'Os participantes do mesmo embate devem pertencer à mesma chave.',
      );
      return;
    }

    confronto.controls.cheNrId.setValue(participant.cheNrId);

    const inscricoes = confronto.controls.inscricaoEmbateFormulario;

    while (inscricoes.length <= side) {
      inscricoes.push(this.createFightGroup());
    }

    if (
      inscricoes.controls.some(
        (fight, fightIndex) =>
          fightIndex !== side &&
          fight.controls.ichNrId?.value === participant.ichNrId,
      )
    ) {
      this.snackbarService.showWarn(
        'O mesmo participante não pode ser adicionado duas vezes no mesmo embate.',
      );
      return;
    }

    this.participantToAdd.next(null);

    const fightControl = inscricoes.at(side) as FormGroup<
      ControlsOf<ClashRegistrationFightForm>
    >;
    fightControl.controls.ichNrId?.setValue(participant.ichNrId);

    fightControl.controls.estTxNome?.setValue(participant.estTxNome);
    fightControl.controls.entTxMetadado?.setValue(MetadataPutEnum.CRIADO);
    inscricoes.updateValueAndValidity();

    this.submitConfrontoIfReady(confronto);
  }

  private submitConfrontoIfReady(
    confronto: FormGroup<EmbateFormControls>,
  ): void {
    const fightControls = confronto.controls.inscricaoEmbateFormulario
      .controls as FormGroup<ControlsOf<ClashRegistrationFightForm>>[];
    const filledFights = fightControls.filter(
      (fight) => fight.controls.ichNrId?.value != null,
    );

    if (filledFights.length !== 2) {
      return;
    }

    const form = this.buildClashBaseForm(confronto, filledFights);
    if (form != null) {
      this.sendConfronto(form, confronto);
    }
  }

  private buildClashBaseForm(
    confronto: FormGroup<EmbateFormControls>,
    fightControls: FormGroup<ControlsOf<ClashRegistrationFightForm>>[],
  ): ClashBaseForm | null {
    const subscriptions = fightControls.map((fight) =>
      this.buildFightForm(fight),
    );
    const entNrId = subscriptions[0]?.entNrId;
    const rodNrId = confronto.controls.rodNrId.value;

    if (rodNrId == null) {
      return null;
    }

    return {
      entNrId: entNrId ?? undefined,
      cheNrId: confronto.controls.cheNrId.value ?? undefined,
      rodNrId: rodNrId,
      petNrId: this.petNrId,
      inscricaoEmbateFormulario: subscriptions,
      entTxMetadado: entNrId
        ? MetadataPutEnum.ALTERADO
        : MetadataPutEnum.CRIADO,
    };
  }

  private buildFightForm(
    fight: FormGroup<ControlsOf<ClashRegistrationFightForm>>,
  ): ClashRegistrationFightForm {
    return {
      entNrId: fight.controls.entNrId?.value ?? undefined,
      ichNrId: fight.controls.ichNrId?.value ?? undefined,
      embNrIdOrigem: fight.controls.embNrIdOrigem?.value ?? undefined,
      iemTxNome: fight.controls.iemTxNome?.value ?? undefined,
      iemTxNomeDefault: fight.controls.iemTxNomeDefault?.value ?? undefined,
      estTxNome: fight.controls.estTxNome?.value ?? undefined,
      iemNrVencedor: fight.controls.iemNrVencedor?.value ?? undefined,
      iemBlSorteio: fight.controls.iemBlSorteio?.value ?? undefined,
      entTxMetadado: fight.controls.entTxMetadado?.value ?? undefined,
    };
  }

  private sendConfronto(
    form: ClashBaseForm,
    confronto: FormGroup<EmbateFormControls>,
  ): void {
    this.#loadingService.activate();
    const request$: Observable<ResponseDto<ClashSaveResponse>> = form.entNrId
      ? this.#clashRegistrationService.updateRegistraionClash(form)
      : this.#clashRegistrationService.saveRegistraionClash(form);

    request$
      .pipe(
        this.takeUntilDestroy(),
        finalize(() => this.#loadingService.deactivate()),
      )
      .subscribe({
        next: (response) => {
          const responseBody = response.response;
          if (!form.entNrId && responseBody?.embNrId != null) {
            const savedEntNrId = Number(responseBody.embNrId);
            confronto.controls.inscricaoEmbateFormulario.controls.forEach(
              (fight) => {
                fight.controls.entNrId?.setValue(savedEntNrId);
                fight.controls.entTxMetadado?.setValue(
                  MetadataPutEnum.INALTERADO,
                );
              },
            );
          }

          this.snackbarService.showSuccess(
            form.entNrId
              ? 'Confronto atualizado com sucesso.'
              : 'Confronto salvo com sucesso.',
          );

          //this.getClashs()
        },
        error: (error) => this.messageErrorResolver.resolveMessage(error),
      });
  }

  removeParticipant(index: number, sideLabel: 'A' | 'B') {
    const side = sideLabel === 'A' ? 0 : 1;
    const confronto = this.confrontosFa.at(index);
    const inscricoes = confronto.controls.inscricaoEmbateFormulario;
    const fightControl = inscricoes.at(side) as FormGroup<
      ControlsOf<ClashRegistrationFightForm>
    >;

    fightControl.controls.ichNrId?.setValue(null);
    fightControl.controls.estTxNome?.setValue(null)
    fightControl.controls.entTxMetadado?.setValue(null);
    inscricoes.updateValueAndValidity();

    const hasAnyParticipant = inscricoes.controls.some(
      (fight) => fight.controls.ichNrId?.value != null,
    );
    if (!hasAnyParticipant) {
      confronto.controls.cheNrId.setValue(null);
    }
  }

  changeRodadasPage(arg0: 'next' | 'prev') {
    const current = this.rodadaCurrentIndex.getValue();
    switch (arg0) {
      case 'prev':
        if (current > 0) {
          this.rodadaCurrentIndex.next(current - 1);
        }
        break;
      case 'next':
        if (current == (this.rodadaDtoList?.length || 0) - 1) return;
        this.rodadaCurrentIndex.next(current + 1);
        break;
    }
  }
  subscribeKeyChange() {
    // this.selectedKey?.subscribe(() => {
    //   this.loadEnrollments();
    // });
  }

  subscribeRodadaPageChange() {
    this.rodadaCurrentIndex
      .pipe(
        switchMap((index) => {
          this.rodadaDtoView = this.rodadaDtoList?.[index];
          return of(this.rodadaDtoView?.rodNrId);
        }),
      )
      .subscribe((rodNrId) => {
        this.getClashs(rodNrId);
      });
  }

  showConfrontoForCurrentRodada(
    confronto: FormGroup<EmbateFormControls>,
  ): boolean {
    return confronto.controls.rodNrId.value === this.rodadaDtoView?.rodNrId;
  }

   openGamesTable() {
      const filter = {
        petNrId: this.petNrId,
        cheNrId: this.selectedKey?.value?.cheNrId,
      };
      this.router.navigate([`/tabela-jogos`], {
        queryParams: { filter: JSON.stringify(filter) },
      });
  }


  private getClashs(rodNrId?: number) {
    if (!rodNrId) {
      rodNrId = this.rodadaDtoView?.rodNrId;
      if (!rodNrId) {
        this.snackbarService.showWarn('Sem rodada selecionada ou cadastrada');
        return;
      }
    }
    this.#clashRegistrationService
      .getRegistraionClash(
        { petNrIds: [this.petNrId], rodNrId: rodNrId },
        { page: 0, size: 999 },
      )
      .subscribe({
        next: (res) => {
          this.embatesRodadaList = res.content;
          this.setConfrontoForm();
        },
        error: (err) => {
          this.messageErrorResolver.resolveMessage(err);
        },
      });
  }

  loadRounds() {
    this.#loadingService.activate();
    this.#roundService
      .buscarRodadasPorEtapa(this.petNrId)
      .pipe(
        tap((rodadas) => {
          this.rodadaDtoList = rodadas.response;
          this.rodadaDtoView =
            this.rodadaDtoList[this.rodadaCurrentIndex.value];
        }),
        extractResponse(),
        finalize(() => this.#loadingService.deactivate()),
        catchError((err) => {
          this.messageErrorResolver.resolveMessage(err);
          return of([]);
        }),
      )
      .subscribe((_) => this.subscribeRodadaPageChange());
  }

  private loadKeys() {
    this.#loadingService.activate();
    this.#clashService
      .getClashs({ petNrId: this.petNrId }, { page: 0, size: 999 })
      .pipe(
        map((keys) => {
          const content = keys.content ?? [];
          const transformedContent = content.map((key) => ({
            ...key,
            inscricoesChave: key.inscricoesChave?.map((inc) => ({
              ...inc,
              estTxSigla: inc.estTxSigla,
            })),
          }));
          this.keysSubject.next(transformedContent);

          const currentSelectedKey = this.selectedKey?.value;
          const nextSelectedKey =
            currentSelectedKey &&
            transformedContent.find(
              (key) => key.cheNrId === currentSelectedKey.cheNrId,
            )
              ? (transformedContent.find(
                  (key) => key.cheNrId === currentSelectedKey.cheNrId,
                ) ?? null)
              : (transformedContent[0] ?? null);

          if (nextSelectedKey) {
            this.selectedKey?.next(nextSelectedKey);
          }
        }),
        finalize(() => this.#loadingService.deactivate()),
      )
      .subscribe({
        error: (error) => this.messageErrorResolver.resolveMessage(error),
      });
  }

  getEnrollments(cheNrId: number) : InscricaoChaveDto[] | undefined {
    const list = this.keysSubject.value.find(
      (v) => v.cheNrId == cheNrId,
    )?.inscricoesChave;
    this.qtdTotalInc = list?.length || 0;
    return list;
  }

  // private loadEnrollments() {
  //   this.#loadingService.activate();
  //   this.#keyEnrollmentService
  //     .listarInscricoesCompletas(this.petNrId, {
  //       page: 0,
  //       size: 999,
  //     } as IPageable)
  //     .pipe(
  //       this.takeUntilDestroy(),
  //       finalize(() => this.#loadingService.deactivate()),
  //       catchError((err) => {
  //         this.messageErrorResolver.resolveMessage(err);
  //         return EMPTY;
  //       }),
  //       tap((incDto) => {
  //         this.keyEnrollmentsDto = incDto.content ?? [];
  //         const list = this.keyEnrollmentsDto;
  //         let result: {
  //           ichNrId: number;
  //           incNrId: number;
  //           incTxNome: string;
  //           incTxEstado: string;
  //           cheNrId: number;
  //         }[] = [];
  //         this.qtdIncSelecionados = 0;

  //         list.forEach((inc) => {
  //           result.push({
  //             ichNrId: inc.ichNrId,
  //             incNrId: inc.incNrId,
  //             incTxNome: inc.incTxResumo,
  //             incTxEstado: inc.estTxDescricao,
  //             cheNrId: inc.cheNrId,
  //           });
  //         });
  //         const filtered = result.filter(
  //           (item) => item.cheNrId == this.selectedKey?.value?.cheNrId,
  //         );
  //         this.qtdTotalInc = filtered.length;

  //         this.filteredEnrollments = filtered;
  //       }),
  //     )
  //     .subscribe();
  // }

  private setConfrontoForm() {
    this.confrontosFa.clear();

    const embates = this.embatesRodadaList ?? [];
    const rodadaId = this.rodadaDtoView?.rodNrId ?? null;

    embates.forEach((confronto) => {
      const fightGroups = (confronto.embListInscricaoDto ?? []).map((fight) => {
        const fightGroup = this.createFightGroup();
        fightGroup.controls.entNrId?.setValue(confronto.embNrId ?? null);
        fightGroup.controls.ichNrId?.setValue(fight.embateDto?.ichNrId ?? null);
        fightGroup.controls.embNrIdOrigem?.setValue(
          fight.embNrIdOrigem ?? null,
        );
        fightGroup.controls.iemTxNome?.setValue(fight.iemTxNome);
        fightGroup.controls.iemTxNomeDefault?.setValue(fight.iemTxNomeDefault);
        fightGroup.controls.estTxNome?.setValue(fight.estTxNome);
        fightGroup.controls.iemNrVencedor?.setValue(
          fight.iemNrVencedor ?? null,
        );
        fightGroup.controls.iemBlSorteio?.setValue(false);
        fightGroup.controls.entTxMetadado?.setValue(MetadataPutEnum.INALTERADO);
        return fightGroup;
      });

      while (fightGroups.length < 2) {
        fightGroups.push(this.createFightGroup());
      }

      const group = this.fb.group<EmbateFormControls>({
        cheNrId: this.fb.control(
          confronto.cheNrId ?? null,
          Validators.required,
        ),
        cheTxNome: this.fb.control(
          confronto.cheTxNome ?? null,
          Validators.required,
        ),
        chaveEmbateSimplesDto: this.fb.control(
          confronto.chaveEmbateSimplesDto ?? null,
        ),
        rodNrId: this.fb.control(rodadaId, Validators.required),
        inscricaoEmbateFormulario: this.fb.array(
          fightGroups,
          this.duplicateIchNrIdValidator,
        ),
      });

      this.confrontosFa.push(group);
    });

    this.confrontosFa.updateValueAndValidity();
  }

  private refresh() {
    this.loadKeys();
    //this.loadEnrollments();
    this.loadRounds();
  }

  trackStateById(_: number, item: any) {
    return item.estTxSigla;
  }

  trackClashById(_: number, item: GroupKeyDto) {
    return item.cheNrId;
  }

  trackCabecaCahveById(_: number, item: InscricaoEtapaDto) {
    return item.ineNrId;
  }

  trackById(index: number, item: any): number {
    return item.ineNrId;
  }
}
